import { type QuizCurriculumItem, type QuizGenerateItem } from '@/api/ai/type';
import { type AnswerPair } from '@/api/gen/_model/answerPair';
import { type Item } from '@/api/gen/_model/item';
import { extractBareText } from '@/shared/utils/extractBareText';
import { stableHash } from '@/shared/utils/stableHash';
import { type QuizHint } from '@/types/Quiz';

import { MAX_QUIZ_COUNT } from '../../../constant';
import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import { getEffectiveCurriculumList } from '../../../util/curriculumFingerprint';

import { adaptAiGeneratedQuizzesToBuildForm } from './adaptAiGeneratedQuizzesToBuildForm';
import {
  type AiBuilderChatQuestionSnapshot,
  type AiBuilderChatQuestionSnapshotData,
  type AiBuilderChatQuestionSnapshotReplaceEvent,
  type AiBuilderQuestionListRevision,
} from './types';

type BuildQuizListItemWithAiMetadata = BuildQuizListItem & {
  comment?: unknown;
  standard?: unknown;
  difficulty?: unknown;
};

/**
 * `question_snapshot_replace` 이벤트를 폼에 적용한 결과.
 *
 * - `stale`: 요청 전송 이후 사용자가 문항 목록을 편집함 → 낡은 결과이므로 버려야 한다.
 * - `exceedsMaxQuizCount`: 교체 결과가 최대 문항 수를 초과함 → 반영 불가.
 * - `applied`: 정상 반영. 폼을 교체하는 데 필요한 값만 담는다.
 */
export type QuestionSnapshotReplaceOutcome =
  | { status: 'stale' }
  | { status: 'exceedsMaxQuizCount' }
  | {
      status: 'applied';
      /** 이벤트를 반영한 새 문항 목록(전체 교체). */
      nextQuizList: BuildQuizListItem[];
      /** 변경/이동 강조 애니메이션 대상 문항 id. */
      animationQuizIds: string[];
      /** 반영 후 스크롤을 맞출 문항 id(없으면 undefined). */
      scrollTargetQuizId: string | undefined;
    };

/**
 * 현재 문항 목록의 "리비전 토큰"을 만든다.
 *
 * 목록 내용의 안정적 지문(`qlr:<hash>`)으로, 서버는 이 값을 해석하지 않고 그대로
 * echo 한다. 클라이언트는 이 토큰의 일치 여부만으로 "그 사이 목록이 바뀌었는지"를
 * 판정한다. 같은 내용 → 같은 토큰(결정적).
 *
 * @see applyQuestionSnapshotReplace 리비전을 비교해 stale 여부를 판정하는 곳
 */
export const questionListRevisionOf = (
  questions: readonly AiBuilderChatQuestionSnapshot[],
): AiBuilderQuestionListRevision => `qlr:${stableHash(questions)}`;

/**
 * 서버로 보낼 문항/보기 텍스트를 뽑는다.
 *
 * plain 필드(`question`/`choice.text`)는 과거 bare LaTeX(구분자 없음)로
 * 평탄화되어 저장된 stale 값일 수 있다. 반면 `richText`(Tiptap HTML)는
 * `<math-inline data-latex>` 노드로 수식 구조를 보존한다. richText 가 있으면
 * 그쪽에서 `extractBareText`(ZWSP 감쌈)로 재추출해 구분자를 복원한 뒤 보내야,
 * 서버 passthrough echo 후 재등록(prepareTiptapHTML)에서 수식이 살아남는다.
 * richText 가 비면(레거시·optional) plain 값으로 폴백해 본문 유실을 막는다.
 */
const toWireText = (
  plain: string | undefined,
  richText: string | undefined,
): string => (richText ? extractBareText(richText) : plain ?? '');

/**
 * 서버의 `question_snapshot_replace` 이벤트를 현재 폼에 적용할 수 있는지 판정하고,
 * 가능하면 적용에 필요한 값을 계산한다.
 *
 * 세 관문을 순서대로 통과시킨다.
 * 1. apply-boundary stale guard — 이벤트가 기준삼은 리비전(`baseQuestionListRevision`)과
 *    지금 목록의 리비전을 비교한다. 요청 전송과 이벤트 도착 사이에 사용자가 목록을
 *    편집했으면 불일치 → `stale`(사용자 편집을 덮어쓰지 않도록 버린다).
 * 2. 최대 문항 수 초과 → `exceedsMaxQuizCount`.
 * 3. 통과 시 새 목록·애니메이션 대상·스크롤 대상을 계산해 `applied` 로 반환한다.
 *
 * 결과 분기(메시지/알럿/반영)는 호출부가 `status` 로 처리한다.
 */
export const applyQuestionSnapshotReplace = (
  event: AiBuilderChatQuestionSnapshotReplaceEvent,
  {
    currentQuizList,
    maxQuizCount = MAX_QUIZ_COUNT,
  }: {
    currentQuizList: readonly BuildQuizListItem[];
    maxQuizCount?: number;
  },
): QuestionSnapshotReplaceOutcome => {
  if (isQuestionSnapshotUpdateStale(event, currentQuizList)) {
    return { status: 'stale' };
  }

  if (event.questions.length > maxQuizCount) {
    return { status: 'exceedsMaxQuizCount' };
  }

  const nextQuizList = buildNextQuizList(event, currentQuizList);
  const changedOrMovedQuestionIds =
    event.changed_question_ids.length > 0
      ? [...event.changed_question_ids]
      : event.moved_question_ids ?? [];
  const scrollTargetQuizId = getScrollTargetQuizId({
    changeType: event.change_type,
    changedOrMovedQuestionIds,
    currentQuizList,
    nextQuizList,
  });
  const animationQuizIds =
    changedOrMovedQuestionIds.length > 0
      ? changedOrMovedQuestionIds
      : scrollTargetQuizId
      ? [scrollTargetQuizId]
      : [];

  return {
    status: 'applied',
    nextQuizList,
    animationQuizIds,
    scrollTargetQuizId,
  };
};

/**
 * 폼의 문항 목록을 서버 계약 형태의 스냅샷 배열로 직렬화한다.
 *
 * 매 턴 요청에 실어 서버 세션과 동기화하는 "권위 목록"이자, 리비전 토큰의 입력이다.
 * essay 는 정답 필드 없이 문항 본문만 담고, 그 외 타입은 정답까지 직렬화한다.
 */
export const serializeCurrentQuizListToAiBuilderQuestions = (
  quizList: readonly BuildQuizListItem[],
): AiBuilderChatQuestionSnapshot[] => {
  return quizList.flatMap((quiz, index) => {
    const baseSnapshot = {
      question_id: quiz.id,
      number: index + 1,
    };

    switch (quiz.type) {
      case 'multiple':
        return {
          ...baseSnapshot,
          quiz_type: 'multiple',
          data: {
            ...createQuestionDataBase(quiz),
            options: quiz.multipleData.choiceList.map(choice =>
              toWireText(choice.text, choice.richText),
            ),
            answer: quiz.multipleData.choiceList.reduce<string[]>(
              (answers, choice, choiceIndex) => {
                if (choice.isAnswer) {
                  answers.push(String(choiceIndex));
                }

                return answers;
              },
              [],
            ),
          },
        };
      case 'ox':
        return {
          ...baseSnapshot,
          quiz_type: 'ox',
          data: {
            ...createQuestionDataBase(quiz),
            answer: quiz.oxData.answer,
          },
        };
      case 'subjective':
        return {
          ...baseSnapshot,
          quiz_type: 'subjective',
          data: {
            ...createQuestionDataBase(quiz),
            answer: quiz.subjectiveData.answerList.map(answer => answer.text),
          },
        };
      case 'essay':
        return {
          ...baseSnapshot,
          quiz_type: 'essay',
          data: createQuestionDataBase(quiz),
        };
      case 'match':
        return {
          ...baseSnapshot,
          quiz_type: 'match',
          data: {
            ...createQuestionDataBase(quiz),
            leftItems: quiz.matchData.leftItems,
            rightItems: quiz.matchData.rightItems,
            answerPairs: quiz.matchData.answerPairs,
          },
        };
      case 'order':
        return {
          ...baseSnapshot,
          quiz_type: 'order',
          data: {
            ...createQuestionDataBase(quiz),
            options:
              quiz.orderData?.items.map(item =>
                toWireText(item.text, item.richText),
              ) ?? [],
            answer:
              quiz.orderData?.items.map((_, itemIndex) => String(itemIndex)) ??
              [],
          },
        };
    }
  });
};

/** 이벤트가 기준삼은 리비전과 현재 목록의 리비전이 어긋나면(=그 사이 편집) true. */
const isQuestionSnapshotUpdateStale = (
  event: AiBuilderChatQuestionSnapshotReplaceEvent,
  currentQuizList: readonly BuildQuizListItem[],
): boolean => {
  if (!event.baseQuestionListRevision) return false;

  return (
    questionListRevisionOf(
      serializeCurrentQuizListToAiBuilderQuestions(currentQuizList),
    ) !== event.baseQuestionListRevision
  );
};

/** 이벤트 스냅샷을 폼 문항 목록으로 변환하고 id·origin 을 복원한다. */
const buildNextQuizList = (
  event: AiBuilderChatQuestionSnapshotReplaceEvent,
  currentQuizList: readonly BuildQuizListItem[],
): BuildQuizListItem[] => {
  const currentQuizById = new Map(
    currentQuizList.map(quiz => [quiz.id, quiz] as const),
  );
  const changedQuestionIdSet = new Set(event.changed_question_ids);
  const generatedQuizList = event.questions.map(question => {
    const currentQuiz = currentQuizById.get(question.question_id);

    // questions_updated 는 전체 스냅샷이지만, 기존 작성 중 draft 는 생성 결과가
    // 아니다. 변경되지 않은 문항은 로컬 폼 값을 그대로 보존해 빈 지문/보기 같은
    // 중간 편집 상태가 새 AI 결과의 적용까지 막지 않게 한다.
    if (currentQuiz && !changedQuestionIdSet.has(question.question_id)) {
      return currentQuiz;
    }

    const [generatedQuiz] = adaptAiGeneratedQuizzesToBuildForm([
      toQuizGenerateItem(question),
    ]);
    if (generatedQuiz == null) {
      throw new Error('Missing generated AI builder question');
    }

    return {
      ...generatedQuiz,
      id: question.question_id,
    };
  });

  return applyQuestionSnapshotOrigins({
    currentQuizList,
    nextQuizList: generatedQuizList,
    changedQuestionIds: event.changed_question_ids,
    generationId: event.generationId,
  });
};

const getScrollTargetQuizId = ({
  changeType,
  changedOrMovedQuestionIds,
  currentQuizList,
  nextQuizList,
}: {
  changeType: AiBuilderChatQuestionSnapshotReplaceEvent['change_type'];
  changedOrMovedQuestionIds: string[];
  currentQuizList?: readonly BuildQuizListItem[];
  nextQuizList: readonly BuildQuizListItem[];
}): string | undefined => {
  const changedQuestionIdSet = new Set(changedOrMovedQuestionIds);
  const changedExistingQuiz = nextQuizList.find(quiz =>
    changedQuestionIdSet.has(quiz.id),
  );

  if (changedExistingQuiz) return changedExistingQuiz.id;

  if (changeType === 'delete' || changeType === 'reduce') {
    return getDeletionScrollTargetQuizId({
      currentQuizList,
      nextQuizList,
    });
  }

  return undefined;
};

const getDeletionScrollTargetQuizId = ({
  currentQuizList,
  nextQuizList,
}: {
  currentQuizList?: readonly BuildQuizListItem[];
  nextQuizList: readonly BuildQuizListItem[];
}): string | undefined => {
  if (currentQuizList == null || currentQuizList.length === 0)
    return nextQuizList.at(-1)?.id;

  const nextQuizIdSet = new Set(nextQuizList.map(quiz => quiz.id));
  const firstDeletedIndex = currentQuizList.findIndex(
    quiz => !nextQuizIdSet.has(quiz.id),
  );

  if (firstDeletedIndex === -1) return nextQuizList.at(-1)?.id;

  return (
    nextQuizList[firstDeletedIndex]?.id ??
    nextQuizList[firstDeletedIndex - 1]?.id ??
    nextQuizList.at(-1)?.id
  );
};

const applyQuestionSnapshotOrigins = ({
  currentQuizList,
  nextQuizList,
  changedQuestionIds,
  generationId,
}: {
  currentQuizList?: readonly BuildQuizListItem[];
  nextQuizList: BuildQuizListItem[];
  changedQuestionIds: readonly string[];
  generationId?: string;
}): BuildQuizListItem[] => {
  const previousOriginById = new Map(
    (currentQuizList ?? []).map(quiz => [quiz.id, quiz.origin] as const),
  );
  const changedQuestionIdSet = new Set(changedQuestionIds);

  return nextQuizList.map(quiz => {
    if (generationId && changedQuestionIdSet.has(quiz.id)) {
      return {
        ...quiz,
        origin: { type: 'ai', generationId },
      };
    }

    const previousOrigin = previousOriginById.get(quiz.id);
    if (!previousOrigin) return quiz;

    return {
      ...quiz,
      origin: previousOrigin,
    };
  });
};

const createQuestionDataBase = (
  quiz: BuildQuizListItem,
): AiBuilderChatQuestionSnapshotData => {
  const quizWithMetadata: BuildQuizListItemWithAiMetadata = quiz;
  const data: AiBuilderChatQuestionSnapshotData = {
    question: toWireText(quiz.question, quiz.questionRichText),
    comment: getString(quizWithMetadata.comment) ?? '',
    standard: getStringArray(quizWithMetadata.standard) ?? [],
  };
  const difficulty = getNumber(quizWithMetadata.difficulty);
  const hint = getQuizHint(quiz.hint);
  if (hint !== undefined) data.hint = hint;
  if (difficulty !== undefined) data.difficulty = difficulty;
  // 서버 세션 스냅샷과 왕복 시 성취기준 쌍을 보존한다 (wire 는 snake_case).
  // 단, 성취기준 부여 이후 편집된 문항은 매핑 유효성이 깨졌으므로 제외한다.
  // 제외하지 않으면 서버 echo 왕복 시 복원 지점에서 새 지문이 찍혀
  // "편집됨" 판정이 세탁된다.
  const curriculumList = getEffectiveCurriculumList(quiz);
  if (curriculumList != null && curriculumList.length !== 0) {
    data.curriculum_list = curriculumList.map(item => ({
      standard_code: item.standardCode,
      subject: item.subject,
    }));
  }

  return data;
};

const toQuizGenerateItem = (
  snapshot: AiBuilderChatQuestionSnapshot,
): QuizGenerateItem => {
  const data = snapshot.data;
  const question = getString(data.question) ?? getString(data.title);

  if (snapshot.quiz_type === 'match') {
    const leftItems = getMatchItems(data.leftItems);
    const rightItems = getMatchItems(data.rightItems);
    const answerPairs = getAnswerPairs(data.answerPairs);

    if (!question || !leftItems || !rightItems || !answerPairs) {
      throw new Error(
        `Invalid AI builder question snapshot data: ${snapshot.question_id}`,
      );
    }

    const quizGenerateItem: QuizGenerateItem = {
      question,
      options: [],
      answer: [],
      type: 'match',
      comment: getString(data.comment) ?? '',
      standard: getStringArray(data.standard) ?? [],
      leftItems,
      rightItems,
      answerPairs,
    };

    const difficulty = getNumber(data.difficulty);
    const hint = getQuizHint(data.hint);
    if (hint !== undefined) quizGenerateItem.hint = hint;
    if (difficulty !== undefined) quizGenerateItem.difficulty = difficulty;

    return quizGenerateItem;
  }

  const options =
    getStringArray(data.options) ??
    getStringArray(data.choices) ??
    (snapshot.quiz_type === 'multiple' ? undefined : []);
  const answer =
    snapshot.quiz_type === 'essay'
      ? []
      : getAnswerList(data.answers ?? data.answer, snapshot.quiz_type);

  if (!question || !options || !answer) {
    throw new Error(
      `Invalid AI builder question snapshot data: ${snapshot.question_id}`,
    );
  }

  const quizGenerateItem: QuizGenerateItem = {
    question,
    options,
    answer,
    type: snapshot.quiz_type,
    comment: getString(data.comment) ?? '',
    standard: getStringArray(data.standard) ?? [],
  };

  const difficulty = getNumber(data.difficulty);
  const hint = getQuizHint(data.hint);
  if (hint !== undefined) quizGenerateItem.hint = hint;
  if (difficulty !== undefined) quizGenerateItem.difficulty = difficulty;
  // 서버 스냅샷은 snake_case(curriculum_list/standard_code)가 정본이다 — SSE
  // 경로는 axios objectToCamel 인터셉터를 타지 않으므로 여기서 명시 변환한다.
  // 서버 passthrough 구현에 따라 camelCase(curriculumList)로 올 수도 있어
  // 두 표기를 모두 수용한다.
  const curriculumList = getCurriculumList(
    data.curriculum_list ?? data.curriculumList,
  );
  if (curriculumList !== undefined) {
    quizGenerateItem.curriculumList = curriculumList;
  }

  return quizGenerateItem;
};

const getString = (value: unknown): string | undefined => {
  return typeof value === 'string' ? value : undefined;
};

const getNumber = (value: unknown): number | undefined => {
  return typeof value === 'number' ? value : undefined;
};

const getStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  if (value.some(item => !isStringLike(item))) return undefined;

  return value.map(String);
};

const getCurriculumList = (
  value: unknown,
): QuizCurriculumItem[] | undefined => {
  if (!Array.isArray(value)) return undefined;

  const items: QuizCurriculumItem[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') return undefined;
    const record = entry as Record<string, unknown>;
    const standardCode =
      getString(record.standard_code) ?? getString(record.standardCode);
    if (!standardCode) return undefined;
    items.push({ standardCode, subject: getString(record.subject) ?? '' });
  }

  return items;
};

const getQuizHint = (value: unknown): QuizHint | undefined => {
  if (!value || typeof value !== 'object') return undefined;

  const hint = value as Record<string, unknown>;
  const type = hint.type;
  const hintValue = getString(hint.value);

  if (type !== 'text' && type !== 'image' && type !== 'youtube') {
    return undefined;
  }
  if (!hintValue) return undefined;

  return { type, value: hintValue };
};

const getMatchItems = (value: unknown): Item[] | undefined => {
  if (!Array.isArray(value)) return undefined;

  const items: Item[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') return undefined;
    const itemRecord = item as Record<string, unknown>;
    const id = getString(itemRecord.id);
    const text = getString(itemRecord.text);
    if (!id || text === undefined) return undefined;

    items.push({
      id,
      text,
      ...(getString(itemRecord.richText) !== undefined && {
        richText: getString(itemRecord.richText),
      }),
      ...(getString(itemRecord.equation) !== undefined && {
        equation: getString(itemRecord.equation),
      }),
      ...(getString(itemRecord.imageUrl) !== undefined && {
        imageUrl: getString(itemRecord.imageUrl),
      }),
    });
  }

  return items;
};

const getAnswerPairs = (value: unknown): AnswerPair[] | undefined => {
  if (!Array.isArray(value)) return undefined;

  const answerPairs: AnswerPair[] = [];
  for (const pair of value) {
    if (!pair || typeof pair !== 'object') return undefined;
    const pairRecord = pair as Record<string, unknown>;
    const leftId = getString(pairRecord.leftId);
    const rightId = getString(pairRecord.rightId);
    if (!leftId || !rightId) return undefined;

    answerPairs.push({ leftId, rightId });
  }

  return answerPairs;
};

const getAnswerList = (
  value: unknown,
  quizType: AiBuilderChatQuestionSnapshot['quiz_type'],
): string[] | undefined => {
  if (Array.isArray(value)) {
    const answers: string[] = [];

    for (const answer of value) {
      const normalizedAnswer = normalizeAnswer(answer, quizType);

      if (normalizedAnswer == null) return undefined;

      answers.push(normalizedAnswer);
    }

    return answers;
  }

  const answer = normalizeAnswer(value, quizType);
  return answer ? [answer] : undefined;
};

const normalizeAnswer = (
  value: unknown,
  quizType: AiBuilderChatQuestionSnapshot['quiz_type'],
): string | undefined => {
  if (value == null) return undefined;
  if (quizType !== 'ox') {
    return isStringLike(value) ? String(value) : undefined;
  }

  if (typeof value === 'boolean') {
    return value ? 'o' : 'x';
  }

  if (!isStringLike(value)) return undefined;

  const normalizedValue = String(value).trim().toLowerCase();

  if (
    normalizedValue === 'true' ||
    normalizedValue === '1' ||
    normalizedValue === 'o'
  ) {
    return 'o';
  }

  if (
    normalizedValue === 'false' ||
    normalizedValue === '0' ||
    normalizedValue === 'x'
  ) {
    return 'x';
  }

  return undefined;
};

const isStringLike = (value: unknown): value is string | number => {
  return typeof value === 'string' || typeof value === 'number';
};
